/**
 * Fusion — collection filtering and sorting
 *
 * Applies Shopify's native storefront filters without a full page reload:
 * the form is serialised to a query string, the section is re-rendered server
 * side, and only the changed regions are swapped in.
 *
 * Everything here is progressive enhancement. The underlying markup is a real
 * <form> with real links, so filtering works with JavaScript disabled.
 */

(function () {
  'use strict';

  class FusionFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.results = this.querySelector('[data-facets-results]');
      this.status = this.querySelector('[data-facets-status]');
      this.panel = this.querySelector('[data-facets-panel]');
      this.overlay = this.querySelector('[data-facets-overlay]');

      this.bind();
      this.bindDrawer();

      // Browser back/forward must restore the matching result set
      window.addEventListener('popstate', () => {
        this.render(window.location.search, false);
      });
    }

    /* -- Binding ---------------------------------------------------------- */

    bind() {
      // Checkbox and price changes
      this.form?.addEventListener('change', (event) => {
        if (!event.target.matches('[data-facet-input], [data-facets-sort]')) return;
        this.apply();
      });

      // Sort control lives outside the form in the sidebar layout
      this.querySelectorAll('[data-facets-sort]').forEach((select) => {
        select.addEventListener('change', () => this.apply());
      });

      // Without JS these are plain links; with JS they filter in place
      this.querySelectorAll('[data-facet-remove], [data-facet-clear]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const url = new URL(link.href, window.location.origin);
          this.render(url.search, true);
        });
      });

      const loadMore = this.querySelector('[data-facets-load-more]');
      loadMore?.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadMore(loadMore);
      });

      // Submitting the form (Enter in a price field) should filter, not reload
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });

      // Both re-run on every bind() call, i.e. after every AJAX filter/sort
      // swap, because that swap replaces the grid and pagination link with
      // fresh server-rendered ones that know nothing of the visitor's chosen
      // view mode or of the previous IntersectionObserver.
      this.initViewToggle();
      this.initInfiniteScroll();
    }

    /* -- Grid / list view toggle ------------------------------------------- */

    initViewToggle() {
      const grid = this.querySelector('[data-collection-grid]');
      const buttons = Array.from(this.querySelectorAll('[data-view-button]'));
      if (!grid || buttons.length === 0) return;

      // A stored preference overrides the section's own default_view setting
      let stored = null;
      try {
        stored = window.localStorage.getItem('fusion:collection-view');
      } catch (error) {
        // Private browsing or a full quota — fall back to the section default
      }
      if (stored) this.setView(grid, buttons, stored);

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const mode = button.dataset.viewButton;
          this.setView(grid, buttons, mode);
          try {
            window.localStorage.setItem('fusion:collection-view', mode);
          } catch (error) {
            // Non-fatal — the choice just will not persist to the next page
          }
        });
      });
    }

    setView(grid, buttons, mode) {
      grid.classList.toggle('collection__grid--list', mode === 'list');
      buttons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.viewButton === mode));
      });
    }

    /* -- Infinite scroll ----------------------------------------------------
       Reuses the same loadMore() the "Load more" button mode uses, so there is
       one code path for both: the sentinel link is a real, hidden <a href>
       that works with JavaScript disabled, and here it is triggered by
       visibility instead of a click.
    ------------------------------------------------------------------------- */

    initInfiniteScroll() {
      const sentinel = this.querySelector('[data-infinite-scroll]');
      if (!sentinel || !('IntersectionObserver' in window)) return;

      this.infiniteObserver?.disconnect();
      this.infiniteObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.infiniteObserver.unobserve(entry.target);
            this.loadMore(entry.target, true);
          });
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.infiniteObserver.observe(sentinel);
    }

    bindDrawer() {
      const toggle = this.querySelector('[data-facets-toggle]');
      const close = this.querySelector('[data-facets-close]');

      toggle?.addEventListener('click', () => this.openPanel(toggle));
      close?.addEventListener('click', () => this.closePanel());
      this.overlay?.addEventListener('click', () => this.closePanel());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-panel-open')) {
          this.closePanel();
        }
      });
    }

    openPanel(trigger) {
      this.classList.add('is-panel-open');
      trigger?.setAttribute('aria-expanded', 'true');
      this.overlay?.classList.add('is-active');
      window.Fusion.lockScroll();
      this.releaseFocus = window.Fusion.trapFocus(
        this.panel,
        this.querySelector('[data-facets-close]')
      );
    }

    closePanel() {
      if (!this.classList.contains('is-panel-open')) return;

      this.classList.remove('is-panel-open');
      this.querySelector('[data-facets-toggle]')?.setAttribute('aria-expanded', 'false');
      this.overlay?.classList.remove('is-active');
      window.Fusion.unlockScroll();
      this.releaseFocus?.();
      this.releaseFocus = null;
    }

    /* -- Filtering -------------------------------------------------------- */

    /** Builds the query string from the current form state. */
    buildSearchParams() {
      const params = new URLSearchParams();

      if (this.form) {
        new FormData(this.form).forEach((value, key) => {
          // Empty price inputs must not become "filter.v.price.gte="
          if (value === '') return;
          params.append(key, value);
        });
      }

      // Sidebar layout renders the sort control outside the form
      const sort = this.querySelector('[data-facets-sort]');
      if (sort && sort.value && !params.has('sort_by')) {
        params.set('sort_by', sort.value);
      }

      return `?${params.toString()}`;
    }

    apply() {
      this.render(this.buildSearchParams(), true);
    }

    async render(search, pushState) {
      this.setLoading(true);

      try {
        const url = `${window.location.pathname}${search}`;
        const response = await fetch(`${url}${search ? '&' : '?'}section_id=${this.sectionId}`);
        if (!response.ok) throw new Error(`Filter request failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        this.swap(html, '[data-facets-results]');
        this.swap(html, '[data-active-facets]');
        this.swap(html, '[data-facets-count]');
        this.swapFilters(html);

        if (pushState) window.history.pushState({ search }, '', url);

        // Rebind: every swapped region brought fresh, unbound nodes with it
        this.form = this.querySelector('[data-facets-form]');
        this.results = this.querySelector('[data-facets-results]');
        this.bind();

        this.announce();
        window.Fusion.initScrollAnimations();
      } catch (error) {
        console.error('[Fusion] Filtering failed, falling back to a page load.', error);
        window.location.search = search;
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Replaces a region with its freshly rendered counterpart.
     * Handles the case where the region exists in one document but not the
     * other — active filter chips disappear entirely when all filters clear.
     */
    swap(html, selector) {
      const fresh = html.querySelector(selector);
      const existing = this.querySelector(selector);

      if (fresh && existing) {
        existing.replaceWith(fresh);
      } else if (!fresh && existing) {
        existing.remove();
      } else if (fresh && !existing) {
        // Chips reappear above the results
        this.querySelector('[data-facets-results]')?.before(fresh);
      }
    }

    /**
     * Filter option counts and availability change with every selection, so
     * the panel contents are replaced — but the open/closed state of each
     * group is preserved so the panel does not collapse under the customer.
     */
    swapFilters(html) {
      const fresh = html.querySelector('[data-facets-form]');
      const existing = this.querySelector('[data-facets-form]');
      if (!fresh || !existing) return;

      const openState = Array.from(existing.querySelectorAll('.facets__group')).map((d) => d.open);

      existing.replaceWith(fresh);

      fresh.querySelectorAll('.facets__group').forEach((group, i) => {
        if (openState[i] !== undefined) group.open = openState[i];
      });
    }

    /**
     * @param {boolean} silent - true when triggered by the infinite-scroll
     *   observer rather than a click: suppresses the focus jump (a passive
     *   scroll should never yank focus) and shows the inline spinner instead
     *   of the button's own loading state.
     */
    async loadMore(button, silent) {
      const spinner = this.querySelector('[data-infinite-spinner]');
      if (silent) {
        spinner?.classList.add('is-visible');
      } else {
        button.classList.add('is-loading');
      }
      button.setAttribute('aria-disabled', 'true');

      try {
        const url = new URL(button.href, window.location.origin);
        const response = await fetch(`${url.pathname}${url.search}&section_id=${this.sectionId}`);
        if (!response.ok) throw new Error(`Load more failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        const freshItems = html.querySelectorAll('.collection__grid > li');
        const grid = this.querySelector('.collection__grid');
        const firstNew = freshItems[0];

        freshItems.forEach((item) => grid?.appendChild(item));

        // Replace the button with the next page's, or drop it on the last page
        const freshButton = html.querySelector('[data-facets-load-more]');
        if (freshButton) {
          button.replaceWith(freshButton);
          if (silent) {
            this.infiniteObserver?.observe(freshButton);
          } else {
            freshButton.addEventListener('click', (event) => {
              event.preventDefault();
              this.loadMore(freshButton);
            });
          }
        } else {
          button.remove();
        }

        // Move focus to the first newly added product so keyboard users are
        // not dumped back at the top of the page — but only for an explicit
        // click; an automatic infinite-scroll load must not steal focus from
        // wherever the visitor's attention already is.
        if (!silent) {
          firstNew?.querySelector('a')?.focus({ preventScroll: true });
        }

        window.Fusion.initScrollAnimations();
        this.announce();
      } catch (error) {
        console.error('[Fusion] Load more failed.', error);
        window.location.href = button.href;
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        spinner?.classList.remove('is-visible');
      }
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      this.results?.setAttribute('aria-busy', String(loading));
    }

    announce() {
      if (!this.status) return;
      const count = this.querySelector('[data-facets-count]');
      this.status.textContent = count ? count.textContent.trim() : '';
    }
  }

  if (!customElements.get('fusion-facets')) {
    customElements.define('fusion-facets', FusionFacets);
  }
})();
